import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';

const SECRET_KEY = 'tabtrips_hotel_secret';
const TOKEN_COOKIE_KEY = 'token';

function getDecryptedToken() {
  const encrypted = Cookies.get(TOKEN_COOKIE_KEY);
  if (!encrypted) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
}

/** Shared across hook instances so Sidebar remounts / Strict Mode don't spam getInvoice. */
let sharedPromise = null;
let sharedHotelId = null;
let sharedHasUnpaid = false;
let sharedFetchedAt = 0;
const CACHE_MS = 15000;
const listeners = new Set();

function notify(hasUnpaid) {
  listeners.forEach((fn) => {
    try {
      fn(hasUnpaid);
    } catch {
      /* ignore */
    }
  });
}

async function checkUnpaidShared(force = false) {
  const token = getDecryptedToken();
  const hotelId = localStorage.getItem('selectedHotelId');

  if (!token || !hotelId) {
    sharedHasUnpaid = false;
    sharedHotelId = null;
    notify(false);
    return false;
  }

  const now = Date.now();
  if (
    !force &&
    sharedHotelId === hotelId &&
    now - sharedFetchedAt < CACHE_MS
  ) {
    return sharedHasUnpaid;
  }

  if (!force && sharedPromise && sharedHotelId === hotelId) {
    return sharedPromise;
  }

  sharedHotelId = hotelId;
  sharedPromise = (async () => {
    const response = await axios.post(
      'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getInvoice',
      { hotel_id: hotelId },
      {
        headers: { 'Content-Type': 'application/json' },
        timeout: 10000,
      }
    );

    let hasUnpaid = false;
    if (response.data?.status && response.data.invoice) {
      const invoices = Array.isArray(response.data.invoice)
        ? response.data.invoice
        : [response.data.invoice];
      hasUnpaid = invoices.some(
        (invoice) => invoice.status && String(invoice.status).toLowerCase() !== 'paid'
      );
    }

    sharedHasUnpaid = hasUnpaid;
    sharedFetchedAt = Date.now();
    notify(hasUnpaid);
    return hasUnpaid;
  })();

  try {
    return await sharedPromise;
  } finally {
    sharedPromise = null;
  }
}

const useUnpaidInvoices = () => {
  const [hasUnpaidInvoices, setHasUnpaidInvoices] = useState(sharedHasUnpaid);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const runCheck = useCallback(async (force = false) => {
    try {
      if (mountedRef.current) {
        setLoading(true);
        setError(null);
      }
      const hasUnpaid = await checkUnpaidShared(force);
      if (mountedRef.current) setHasUnpaidInvoices(hasUnpaid);
      return hasUnpaid;
    } catch (err) {
      console.error('Error checking unpaid invoices:', err);
      if (mountedRef.current) {
        setError(err.message);
        setHasUnpaidInvoices(false);
      }
      return false;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const onUpdate = (hasUnpaid) => {
      if (mountedRef.current) setHasUnpaidInvoices(hasUnpaid);
    };
    listeners.add(onUpdate);

    runCheck(false);

    const onHotelSelected = () => runCheck(true);
    window.addEventListener('hotelSelected', onHotelSelected);

    return () => {
      mountedRef.current = false;
      listeners.delete(onUpdate);
      window.removeEventListener('hotelSelected', onHotelSelected);
    };
  }, [runCheck]);

  return {
    hasUnpaidInvoices,
    loading,
    error,
    refetch: () => runCheck(true),
  };
};

export default useUnpaidInvoices;
